import { ChevronLeft, Eye, EyeOff } from 'lucide-preact';
import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { api } from '../../services/api.js';

interface SecurityPageProps {
  path?: string;
}

export function SecurityPage(_props: SecurityPageProps) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (newPw !== confirmPw) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api.patch('/users/me/password', {
        current_password: currentPw,
        new_password: newPw,
        confirm_password: confirmPw,
      });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setSuccess(true);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Password change failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-fill bg-bg flex flex-col px-4 pt-6">
      <div className="w-full max-w-sm mx-auto">
        <button
          type="button"
          onClick={() => route('/account')}
          className="flex items-center gap-2 text-gray-400 hover:text-text mb-6"
          aria-label="Back to account"
        >
          <ChevronLeft size={20} aria-hidden="true" />
          <span className="text-sm">Back</span>
        </button>

        <h1 className="text-2xl font-bold text-text mb-2">Security</h1>
        <p className="text-gray-400 mb-8">Change your account password.</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {[
            {
              key: 'current' as const,
              label: 'Current Password',
              value: currentPw,
              setter: setCurrentPw,
              autoComplete: 'current-password',
            },
            {
              key: 'new' as const,
              label: 'New Password',
              value: newPw,
              setter: setNewPw,
              autoComplete: 'new-password',
            },
            {
              key: 'confirm' as const,
              label: 'Confirm New Password',
              value: confirmPw,
              setter: setConfirmPw,
              autoComplete: 'new-password',
            },
          ].map(({ key, label, value, setter, autoComplete }) => (
            <div key={key} className="flex flex-col gap-1">
              <label htmlFor={`sec-${key}`} className="text-sm text-gray-400">
                {label}
              </label>
              <div className="relative">
                <input
                  id={`sec-${key}`}
                  {...(showPw[key] ? { type: 'text' as const } : { type: 'password' as const })}
                  required
                  autoComplete={autoComplete}
                  className="bg-surface-mid text-text rounded-lg px-4 py-3 w-full pr-11"
                  value={value}
                  onInput={(e) => setter((e.target as HTMLInputElement).value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => ({ ...s, [key]: !s[key] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  aria-label={showPw[key] ? 'Hide password' : 'Show password'}
                >
                  {showPw[key] ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          ))}

          {error && <p className="text-sm text-red-400">{error}</p>}
          {success && <p className="text-sm text-green-400">Password updated successfully.</p>}

          <Button type="submit" loading={loading} className="w-full mt-2">
            Update Password
          </Button>
        </form>
      </div>
    </div>
  );
}
